import { RailsMapRoute } from '@map/railsApiRoutes';
import { SUPPORTED_APP_LANGUAGES } from '@shared/appConstants';
import useHttpQuery, { Params as HttpParams, stringifyParams } from '@shared/hooks/useHttpQuery';
import axios from 'axios';
import { DependencyList, useMemo } from 'react';
import useSWR, { SWRConfiguration, mutate, useSWRConfig } from 'swr';
import { useLocaleCode } from './userApi';

const staleKeys = new Set<string>([]);

type MobileApiUrl<T extends string> = T extends `/${string}/api/${infer U}` ? U : never;
export type MobileApiPath = WithOptionalQueryParams<MobileApiUrl<RailsMapRoute>>;

export const buildApiUrl = (
  path: MobileApiPath,
  locale = document.documentElement.lang,
): WithOptionalQueryParams<RailsMapRoute> => {
  const language = SUPPORTED_APP_LANGUAGES.includes(locale) ? locale : locale.split('-')[0];
  return `/${language}/api/${path}` as const;
};

export const useApiUrl = (path: MobileApiPath) => {
  const locale = useLocaleCode();
  return buildApiUrl(path, locale);
};

type Params = {
  path: undefined | MobileApiPath;
  deps?: DependencyList;
};

export const useApiQuery = <T>({ path, ...otherOptions }: Params & Partial<HttpParams>) => {
  const locale = useLocaleCode();
  return useHttpQuery<T>({
    ...otherOptions,
    url: path ? buildApiUrl(path, locale) : null,
  });
};

const apiFetcher = (uri: string) => axios.get(uri).then(({ data }) => data);
const apiFetcherUnpack = (uri: string) => axios.get(uri).then(({ data }) => data.data);

type RemoteDataParams<TData> = {
  url: MobileApiPath | null;
  volatile?: boolean;
  unpackData?: boolean;
  params?: HttpParams['params'];
} & SWRConfiguration<TData>;

export function useRemoteData<TData>(p: RemoteDataParams<TData>) {
  const { url, volatile = false, unpackData = true, params, ...restParams } = p;
  const { cache } = useSWRConfig();
  const locale = useLocaleCode();
  const key = useMemo(() => {
    if (!url) {
      return null;
    }
    const base = buildApiUrl(url, locale);
    const stringifiedParams = stringifyParams(params);
    if (stringifiedParams) {
      return `${base}?${stringifiedParams}`;
    }
    return base;
  }, [locale, params, url]);
  const { data, mutate, ...meta } = useSWR<TData, any, typeof key>(
    key,
    unpackData ? apiFetcherUnpack : apiFetcher,
    {
      revalidateOnMount: key && staleKeys.has(key) ? true : undefined,
      revalidateOnReconnect: volatile,
      revalidateOnFocus: volatile,
      ...restParams,
      fallbackData: volatile || !key ? restParams.fallbackData : cache.get(key)?.data,
      onSuccess: (data, id, config) => {
        restParams.onSuccess?.(data, id, config);
        if (key) {
          return staleKeys.delete(key);
        }
      },
    },
  );
  return [data, mutate, meta] as const;
}

export const remoteDataChanged = (url: MobileApiPath, freshData?: any) => {
  const key = buildApiUrl(url);
  if (freshData) {
    return mutate(key, freshData, false);
  }
  staleKeys.add(key);
  return mutate(key);
};
